// enci.background.js

import { debugLog } from './enci.log.js';

/**
 * @typedef {Object} StyleAttribute
 * @property {string} selector - The data attribute selector
 * @property {string} styleProperty - The CSS property to set
 * @property {function} getValue - Function to get the value from the dataset
 */

/**
 * Definitions for style attributes to be processed.
 * @type {StyleAttribute[]}
 */
const STYLE_ATTRIBUTES = [
  {
    selector: '[data-bg-color]',
    styleProperty: 'backgroundColor',
    getValue: dataset => dataset.bgColor,
  },
  {
    selector: '[data-text-color]',
    styleProperty: 'color',
    getValue: dataset => dataset.textColor,
  },
  {
    selector: '[data-bg-img]',
    styleProperty: 'backgroundImage',
    getValue: dataset => `url('${dataset.bgImg}')`,
  },
];

/**
 * Applies a specific style to elements matching a selector.
 * @param {StyleAttribute} styleAttribute - The style attribute configuration
 */
function applyStyle(styleAttribute) {
  const { selector, styleProperty, getValue } = styleAttribute;
  document.querySelectorAll(selector).forEach(element => {
    element.style[styleProperty] = getValue(element.dataset);
  });
}

/**
 * Dynamically sets the CSS properties of elements based on their data attributes
 * for background color, text color, and background image.
 */
export function setDataBgColor() {
  debugLog('setDataBgColor');
  STYLE_ATTRIBUTES.forEach(applyStyle);
}

/**
 * Converts a hyphenated string to camelCase.
 * @param {string} str - The hyphenated string to convert
 * @returns {string} The camelCase version of the string
 */
function toCamelCase(str) {
  return str.replace(/-([a-z])/g, g => g[1].toUpperCase());
}

/**
 * Initializes background images for elements based on a data attribute.
 * @param {string} dataSelector - The CSS selector for the elements to target
 * @param {string} dataValue - The data attribute name that holds the background image URL
 */
export function initializeDataBackground(dataSelector, dataValue) {
  debugLog(`initializeDataBackground for ${dataSelector} and ${dataValue}`);

  const elements = document.querySelectorAll(dataSelector);

  if (!elements.length) {
    debugLog('initializeDataBackground >> No elements found.', { type: 'error' });
    return;
  }

  const camelCaseDataValue = toCamelCase(dataValue);

  elements.forEach(element => {
    const backgroundImageUrl = element.dataset[camelCaseDataValue];
    if (backgroundImageUrl) {
      element.style.backgroundImage = `url('${backgroundImageUrl}')`;
    }
  });
}
